import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ChevronDown, ChevronUp, Share2, Check, Copy, Zap, Coffee, Search, Sparkles, ArrowUpRight, Globe } from "lucide-react";
import ReactMarkdown from "react-markdown";
import API from "@/lib/api";

const MODES = [
  { key: "quick", icon: Zap, label: "Between meetings", sub: "5 min" },
  { key: "coffee", icon: Coffee, label: "Coffee break", sub: "15 min" },
  { key: "deep", icon: Search, label: "Deep dive", sub: "Full picture" },
];

function TimeModeToggle({ mode, setMode }) {
  return (
    <div className="bg-white border border-zinc-200 p-2 flex gap-1" data-testid="time-mode-toggle">
      {MODES.map((m) => {
        const Icon = m.icon;
        const active = mode === m.key;
        return (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={`flex-1 flex flex-col items-center py-2 px-1 transition-all text-center ${
              active ? "bg-amber-50 border border-amber-700 text-zinc-950" : "border border-transparent hover:bg-zinc-50 text-zinc-900"
            }`}
            data-testid={`mode-${m.key}`}
          >
            <span className="flex items-center gap-1">
              <Icon className="w-3.5 h-3.5" strokeWidth={active ? 2 : 1.5} />
              <span className="text-xs font-medium hidden sm:inline">{m.label}</span>
            </span>
            <span className={`mt-0.5 ${active ? "text-amber-700" : "text-zinc-900"}`} style={{ fontSize: "11px" }}>{m.sub}</span>
          </button>
        );
      })}
    </div>
  );
}

function parseImplication(text) {
  if (!text) return { text, linkedAction: null };
  const match = text.match(/\[linked_action:\s*(\d+)\]/);
  if (!match) return { text, linkedAction: null };
  return { text: text.replace(match[0], "").trim(), linkedAction: parseInt(match[1], 10) };
}

// Wave 11 — keyword-based source matcher.
// Given an action option's text + signal_anchor, find the top 1-2 supporting sources.
// Operates entirely client-side; no LLM cost, deterministic.
const STOPWORDS = new Set([
  "the", "and", "for", "with", "that", "from", "this", "your", "have", "will",
  "are", "was", "were", "has", "had", "can", "its", "their", "they", "them",
  "you", "but", "not", "any", "all", "one", "two", "new", "use", "out", "how",
  "why", "who", "what", "when", "where", "which", "while", "about", "into",
  "over", "under", "more", "most", "some", "such", "than", "then", "these",
  "those", "there", "here", "also", "onto", "upon", "been", "being",
  "does", "doing", "done", "make", "makes", "made", "take", "takes", "took",
  "gets", "put", "set", "saw", "seen", "know", "knows",
  "known", "think", "thinks", "thought", "just", "like", "very", "still",
  "next", "first", "last", "only", "other", "each", "every", "both",
]);

function tokenize(s) {
  if (!s) return [];
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/)
    .filter(w => w.length > 3 && !STOPWORDS.has(w));
}

function matchSourcesForText(queryText, sources) {
  if (!Array.isArray(sources) || !sources.length || !queryText) return [];
  const queryTokens = new Set(tokenize(queryText));
  if (queryTokens.size === 0) return [];

  const scored = sources.map((s, idx) => {
    const sourceTokens = new Set(tokenize(`${s.title || ""} ${s.date || ""}`));
    let overlap = 0;
    for (const t of queryTokens) if (sourceTokens.has(t)) overlap++;
    return { idx, overlap };
  });

  scored.sort((a, b) => b.overlap - a.overlap);
  // Require at least 2 meaningful word matches; cap at top 2 citations per option.
  return scored.filter(s => s.overlap >= 2).slice(0, 2).map(s => s.idx);
}

// Wave 15 — inline source attribution.
//
// Instruction 9 (2026-05-22) — source line is now `headline · publisher · date`
// when `anchor_signal` carries the actual article headline. The headline
// is hyperlinked (URL → Wayback → plain text fallback). Publisher and
// date append as smaller-weight metadata.
//
// Instruction 8 backward compat: when no `source.headline` is present
// (older reveal records where the snapshot only carried outlet+date),
// falls back to the previous "outlet · date" format.
function InlineSource({ source, shouldHyperlink, testId }) {
  if (!source) return null;
  const url = source.url || "";
  // Chunk 3.6 — wayback fallback for non-verified URLs.
  const hasArchive = source.url_verified === false && !!source.wayback_url;
  const hrefUrl = source.url_verified === false
    ? (source.wayback_url || null)
    : (url || null);
  let outlet = source.source_name || "";
  if (!outlet && url) {
    try { outlet = new URL(url).hostname.replace(/^www\./, ""); } catch { outlet = ""; }
  }
  const headline = source.headline || source.title || "";
  // Instruction 10 (2026-05-22) — source-line uniformity rule.
  // The publisher field must NOT duplicate the headline. If outlet
  // is the same string as the headline (or differs only by case /
  // trailing punctuation), suppress it and fall back to the URL
  // domain. Same publisher repeated as both headline AND outlet
  // looks broken to a subscriber.
  const _norm = (s) => String(s || "").replace(/[.\s]+$/, "").trim().toLowerCase();
  if (outlet && headline && _norm(outlet) === _norm(headline)) {
    let domain = "";
    try { if (url) domain = new URL(url).hostname.replace(/^www\./, ""); } catch { /* noop */ }
    outlet = domain || "";  // empty = the trailing " · publisher" segment is dropped
  }
  // Instruction 8 — title-fallback when neither headline nor outlet is set.
  if (!outlet && !headline && !source.date) return null;

  // Build the segment after the headline: " · Publisher · Date" / etc.
  const tailParts = [];
  if (outlet) tailParts.push(outlet);
  if (source.date) tailParts.push(source.date);
  const tailText = tailParts.length ? ` · ${tailParts.join(" · ")}` : "";

  // Instruction 9 — when we have the actual article headline, render the
  // headline as the clickable title with publisher+date trailing.
  // Otherwise legacy outlet+date format.
  const useHeadlineLayout = !!headline;
  const label = useHeadlineLayout ? (
    <>
      <span className="font-medium text-zinc-700">{headline}</span>
      {tailText && <span className="text-zinc-400">{tailText}</span>}
      {hasArchive && (
        <span className="text-zinc-400 italic" title="Linked via archive.org Wayback Machine snapshot."> (archived)</span>
      )}
    </>
  ) : (
    <>
      {outlet}
      {source.date && <span className="text-zinc-400"> · {source.date}</span>}
      {hasArchive && (
        <span className="text-zinc-400 italic" title="Linked via archive.org Wayback Machine snapshot."> (archived)</span>
      )}
    </>
  );

  if (shouldHyperlink && hrefUrl) {
    return (
      <a
        href={hrefUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-start gap-1 mt-1 text-xs text-zinc-500 hover:text-amber-700 transition-colors"
        data-testid={testId}
      >
        <ArrowUpRight className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
        <span className="underline-offset-2 hover:underline">{label}</span>
      </a>
    );
  }
  return (
    <div className="mt-1 text-xs text-zinc-500" data-testid={testId}>
      {label}
    </div>
  );
}

// Wave 15 — SourceCitation + scrollToSource removed; replaced by InlineSource.


// Wave 1: sort signal list by severity group (act_now → watch → context), then priority_score desc
const SEVERITY_ORDER = { act_now: 0, watch: 1, context: 2 };
function sortBySeverity(signals) {
  if (!Array.isArray(signals)) return [];
  return [...signals].sort((a, b) => {
    const sa = SEVERITY_ORDER[a?.severity_tag] ?? 99;
    const sb = SEVERITY_ORDER[b?.severity_tag] ?? 99;
    if (sa !== sb) return sa - sb;
    const pa = a?.priority_score ?? 0;
    const pb = b?.priority_score ?? 0;
    if (pa !== pb) return pb - pa;
    // Tie-break: signal date desc (if present), then headline alpha
    const da = a?.date || a?.signal_date || "";
    const db = b?.date || b?.signal_date || "";
    if (da !== db) return db.localeCompare(da);
    return (a?.headline || "").localeCompare(b?.headline || "");
  });
}

// Wave 4 note: "Feeds Action N" tag removed now that Wave 11 inline source
// citations on action options already surface the linkage explicitly.



function SharePopover({ signal, accountName, weekLabel, onClose }) {
  const ref = useState(null);
  const popRef = useCallback((node) => { ref[1](node); }, [ref]);
  const [copied, setCopied] = useState(false);
  const { text: implText } = parseImplication(signal.implication);
  const shareText = [
    `${accountName} · ${weekLabel}`,
    signal.headline,
    implText || null,
    "",
    "Via Kalib AI — FS account intelligence for teams selling into global FS accounts.",
    "kalib.com",
  ].filter(Boolean).join("\n");

  useEffect(() => {
    const el = ref[0];
    const handleClick = (e) => { if (el && !el.contains(e.target)) onClose(); };
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => { document.removeEventListener("mousedown", handleClick); document.removeEventListener("keydown", handleEsc); };
  }, [ref, onClose]);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(shareText); } catch {
      const ta = document.createElement("textarea"); ta.value = shareText; ta.style.cssText = "position:fixed;opacity:0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => { setCopied(false); onClose(); }, 1200);
  };

  return (
    <div ref={popRef} className="absolute right-0 top-7 z-20 bg-white border border-zinc-200 shadow-md py-1 w-48" data-testid="share-popover">
      <button onClick={handleCopy} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-50 transition-colors" data-testid="share-copy-btn">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> : <Copy className="w-3.5 h-3.5 text-zinc-900 flex-shrink-0" />}
        <span style={{ fontSize: "13px", color: copied ? "#059669" : "#1a1a1a" }}>{copied ? "Copied" : "Copy to clipboard"}</span>
      </button>
    </div>
  );
}

function SignalCard({ signal, index, accountName, weekLabel, mode, isExternal = false, isLite = false, attribution }) {
  const [showShare, setShowShare] = useState(false);
  const { text: implText } = parseImplication(signal.implication);
  const close = useCallback(() => setShowShare(false), []);
  const showImplication = !isExternal && implText;
  const showImplicationShimmer = !isExternal && !implText && isLite;

  // Wave 15 — attribution is precomputed at Reveal level and passed in as { source, shouldHyperlink }.
  const attachedSource = attribution?.source || null;

  return (
    <div className="relative border border-zinc-100 p-4 animate-fade-in group" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`signal-card-${index}`}>
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <button onClick={() => setShowShare((v) => !v)} className="text-zinc-900 hover:text-amber-700 transition-colors" aria-label="Share signal" data-testid={`share-btn-${index}`}>
          <Share2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
      {showShare && <SharePopover signal={signal} accountName={accountName} weekLabel={weekLabel} onClose={close} />}
      <p className="pr-10" style={{ fontSize: "17px", fontWeight: 600, color: "#111111", lineHeight: 1.4 }}>{signal.headline}</p>
      <p className="mt-1.5" style={{ fontSize: "15px", fontWeight: 400, lineHeight: 1.7, color: "#1a1a1a" }}>{signal.context}</p>
      {showImplication && (
        <p className="mt-1.5 italic" style={{ fontSize: "14px", fontWeight: 400, color: "#B45309" }}>
          {implText}
        </p>
      )}
      {showImplicationShimmer && (
        <div className="mt-1.5 animate-pulse" data-testid={`signal-impl-shimmer-${index}`}>
          <div className="h-3 bg-amber-100/70 w-11/12 rounded-sm" />
          <div className="mt-1 h-3 bg-amber-100/70 w-4/6 rounded-sm" />
        </div>
      )}
      {attachedSource && (
        <InlineSource source={attachedSource} shouldHyperlink={!!attribution?.shouldHyperlink} testId={`signal-card-${index}-source`} />
      )}
      {/* Previously Flagged — disabled until 4+ reveals exist for this account */}
      {false && signal.callback_text && mode !== "quick" && (
        <div className="mt-2.5 border-l-2 border-zinc-300 pl-3 py-1.5" data-testid={`callback-${index}`}>
          <p className="uppercase mb-0.5" style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", color: "#111111" }}>Previously flagged</p>
          <p className="italic" style={{ fontSize: "13px", fontWeight: 400, lineHeight: 1.6, color: "#111111" }}>{signal.callback_text}</p>
        </div>
      )}
    </div>
  );
}

// Wave 12 — compact per-option feedback row
const FEEDBACK_STATES = [
  { state: "relevant",      emoji: "✅", label: "Relevant",      selectedClass: "border-emerald-600 bg-emerald-50 text-emerald-900" },
  { state: "not_this_week", emoji: "⏳", label: "Not this week", selectedClass: "border-amber-600 bg-amber-50 text-amber-900" },
  { state: "not_relevant",  emoji: "🚫", label: "Not relevant",  selectedClass: "border-red-600 bg-red-50 text-red-900" },
];

function OptionFeedbackRow({ currentState, onSelect, showHelper, showLabels = true, testIdBase }) {
  return (
    <div className={showLabels ? "flex flex-col items-end gap-1" : "flex items-center"} data-testid={`${testIdBase}-feedback-row`}>
      {showLabels && showHelper && (
        <p className="text-[11px] text-zinc-900" data-testid={`${testIdBase}-feedback-helper`}>
          Tap to help Kalib AI calibrate.
        </p>
      )}
      <div className="flex items-center gap-1">
        {FEEDBACK_STATES.map(({ state, emoji, label, selectedClass }) => {
          const selected = currentState === state;
          // Chunk 3 (a11y) — feedback chips must be ≥44px tap target on mobile
          // (WCAG 2.5.5 / iOS HIG). Vertical sizing handled via min-h; padding
          // sized so chip stays compact on desktop while comfortable on touch.
          const basePad = showLabels ? "px-2.5 py-2" : "px-2 py-2";
          return (
            <button
              key={state}
              type="button"
              onClick={() => onSelect(state)}
              aria-pressed={selected}
              aria-label={label}
              title={label}
              className={`inline-flex items-center justify-center gap-1 ${basePad} min-h-[44px] border text-[11px] font-medium transition-all ${
                selected
                  ? `${selectedClass} font-semibold shadow-sm`
                  : "border-zinc-200 bg-white text-zinc-900 hover:border-zinc-400 hover:bg-zinc-50"
              }`}
              data-testid={`${testIdBase}-feedback-${state}`}
            >
              <span className="leading-none" style={{ fontSize: "13px" }}>{emoji}</span>
              {showLabels && <span>{label}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Wave 7 (Chunk 2.5) — paragraph-schema action card.
// Chunk 3 — collapsed/expanded states, globe marker for cross_connected,
// citation chip row at end of expanded body. Default collapsed except for
// the first action card (controlled by parent via `defaultExpanded` prop).
// =============================================================================

// Strip inline [N] / [N, M, ...] tokens from markdown so they don't render
// in prose. Returns { cleaned, citationNs } — citationNs records which numbers
// were referenced (for callers that still want the list).
//
// Instruction 8 (2026-05-22): citation chip-injection is RETIRED. Cards now
// render their evidence anchor via `card.anchor_signal` (a stable snapshot
// set at synthesis time by `evidence_anchor.enrich_anchor_snapshots`); the
// per-card `CITATIONS [N]` footer is gone, and inline `[N]` tokens are
// always stripped at render. The `sources` parameter is kept for
// signature stability but is no longer used to inject hyperlinks.
function extractCitations(markdown, _sources = null) {
  if (!markdown) return { cleaned: "", citationNs: [] };
  const seen = new Set();
  const order = [];
  // Strip the marker (and any leading whitespace) entirely.
  const cleaned = markdown.replace(/\s*\[(\d+(?:\s*,\s*\d+)*)\]/g, (m, group) => {
    const nums = group.split(/\s*,\s*/).map((x) => parseInt(x, 10)).filter((x) => !isNaN(x));
    nums.forEach((n) => { if (!seen.has(n)) { seen.add(n); order.push(n); } });
    return "";
  })
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\s+([.,;:?!])/g, "$1")
    .replace(/[ \t]{2,}/g, " ");
  return { cleaned, citationNs: order };
}

// Smooth-scroll to a numbered source row in the SourcesList and trigger an
// expand event. Mirrors the Wave 11 source-flash interaction.
function flashSource(n) {
  // Tell SourcesList to expand
  window.dispatchEvent(new CustomEvent("reveal:open-sources", { detail: { n } }));
  setTimeout(() => {
    const el = document.getElementById(`source-${n}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("source-flash");
      setTimeout(() => el.classList.remove("source-flash"), 1800);
    }
  }, 100);
}

function CitationChipRow({ ns, sources }) {
  if (!Array.isArray(ns) || ns.length === 0) return null;
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="citation-chip-row">
      <span className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mr-1">Citations</span>
      {ns.map((n) => {
        const src = sources?.[n - 1];
        const title = src?.title || `Source ${n}`;
        return (
          <button
            key={n}
            type="button"
            onClick={() => flashSource(n)}
            className="inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 text-[11px] font-semibold border border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-700 hover:bg-amber-100 transition-colors"
            title={title}
            data-testid={`citation-chip-${n}`}
          >
            [{n}]
          </button>
        );
      })}
    </div>
  );
}

function ParagraphActionCard({ action, index, sources = [], actionAttribution, actionFeedbackState, onActionFeedback, expanded = false, onToggleExpand }) {
  const title = action.title || "";
  // Instruction 9 (2026-05-22) — visible rank is ALWAYS sequential from
  // the final rendered position. Original `display_rank` is preserved on
  // the action object for feedback-key stability but never displayed.
  // This eliminates the "Card 1 / 3 / 4" gap caused by serve-time
  // calibration / QA drops that mutate the visible card set after
  // pre-drop ranks have been stamped.
  const rank = index + 1;
  // Legacy `primary_signal_anchor` is the paraphrased event summary —
  // Instruction 9 retires it as the source line; the actual headline
  // from `anchor_signal.headline` now owns that slot. Fall back to the
  // paraphrased anchor only when no `anchor_signal` was resolved.
  const anchor = action.anchor_signal && action.anchor_signal.headline
    ? null
    : (action.primary_signal_anchor || action.signal_anchor || "");
  const attachedSource = actionAttribution?.source || null;
  const rawBody = action.body_markdown || "";

  // Wave 9 — prefer the explicit `cited_sources` schema field; fall back to
  // legacy inline `[N]` extraction for back-compat with reveals generated
  // before the schema field existed.
  // Instruction 5 Phase B — pass `sources` so [N] tokens become inline
  // hyperlinks resolved to live URL / wayback / unavailable-sentinel.
  const { cleaned, citationNs } = useMemo(() => {
    const explicit = Array.isArray(action.cited_sources)
      ? action.cited_sources.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n))
      : null;
    const inlined = extractCitations(rawBody, sources);
    if (explicit && explicit.length > 0) {
      // De-dupe in order of first appearance.
      const seen = new Set();
      const order = [];
      explicit.forEach((n) => { if (!seen.has(n)) { seen.add(n); order.push(n); } });
      return { cleaned: inlined.cleaned, citationNs: order };
    }
    return inlined;
  }, [action.cited_sources, rawBody, sources]);

  // Hook preview = first paragraph, citations stripped, line-clamped via CSS.
  // Instruction 5 Phase B — the inline-linked `cleaned` body now contains
  // markdown link syntax `[\[N\]](url)`; for the preview we strip those back
  // to a clean prose snippet (so the collapsed hook reads naturally).
  const previewText = useMemo(() => {
    const firstPara = (cleaned.split("\n\n")[0] || "")
      .replace(/\[\\\[\d+\\\]\]\([^)]*\)/g, "")  // strip `[\[N\]](href)`
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .replace(/\s+([.,;:?!])/g, "$1")
      .replace(/[ \t]{2,}/g, " ");
    return firstPara.trim();
  }, [cleaned]);

  const cardClass = "border-l-4 border-l-amber-700 border border-zinc-200 bg-white";

  const handleExpand = () => {
    if (!expanded) {
      // Telemetry placeholder — real wiring is post-beta.
      // eslint-disable-next-line no-console
      console.log("telemetry:card_expand", {
        card_rank: rank,
        card_title: title,
        cross_connected: !!action.cross_connected,
      });
      setTimeout(() => {
        const el = document.getElementById(`action-${rank}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
    if (onToggleExpand) onToggleExpand();
  };

  return (
    <div
      id={`action-${rank}`}
      className={`${cardClass} p-5 animate-fade-in scroll-mt-24`}
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`action-card-${index}`}
    >
      <div className="flex items-start gap-4">
        <span
          className="flex-shrink-0 inline-flex items-center justify-center rounded-full bg-zinc-950 text-white font-heading font-semibold mt-0.5 print:border print:border-zinc-950"
          style={{ width: "32px", height: "32px", fontSize: "15px", lineHeight: 1, letterSpacing: "-0.01em" }}
          aria-label={`Action ${rank}`}
          data-testid={`action-rank-badge-${index}`}
        >
          {rank}
        </span>
        <div className="flex-1 min-w-0">
          {/* Title — globe marker rendered inline ONLY when card is collapsed AND
              cross_connected. Once expanded, marker hides (cross-connection is
              self-evident in the prose). */}
          <p style={{ fontSize: "17px", fontWeight: 600, color: "#111111", lineHeight: 1.4 }} data-testid={`action-title-${index}`}>
            {!expanded && action.cross_connected && (
              <Globe
                className="inline-block align-[-2px] mr-1.5 text-zinc-700"
                style={{ width: "15px", height: "15px" }}
                strokeWidth={1.75}
                aria-hidden="true"
                data-testid={`action-${index}-cross-connected-icon`}
              />
            )}
            {title}
          </p>

          {/* Anchor + source line — Instruction 8 (2026-05-22).
              Prefer the stable `action.anchor_signal` snapshot (resolved at
              synthesis time by evidence_anchor.enrich_anchor_snapshots).
              Falls back to the legacy positional `attachedSource` for older
              reveals lacking the snapshot.
              Patch B — action.anchor_suppressed=true hides both lines. */}
          {!action.anchor_suppressed && (anchor || action.anchor_signal || attachedSource) && (
            <div className="mt-1" data-testid={`action-anchor-${index}`}>
              {anchor && (
                <p style={{ fontSize: "12px", fontWeight: 500, color: "#B45309" }} data-testid={`action-anchor-text-${index}`}>{anchor}</p>
              )}
              {action.anchor_signal ? (
                <InlineSource
                  source={action.anchor_signal}
                  shouldHyperlink={true}
                  testId={`action-anchor-source-${index}`}
                />
              ) : (
                attachedSource && (
                  <InlineSource
                    source={attachedSource}
                    shouldHyperlink={!!actionAttribution?.shouldHyperlink}
                    testId={`action-anchor-source-${index}`}
                  />
                )
              )}
            </div>
          )}

          {/* "Based on your feedback" boost tag — hidden in v5.1 P0.6 (feedback
              is recording-only until FEEDBACK_CALIBRATION_ENABLED flips true). */}
          {false && action.boosted_by_feedback && (
            <div className="mt-1.5 flex items-center gap-1" data-testid={`action-${index}-boosted-tag`}>
              <Sparkles className="w-3 h-3 text-amber-700" strokeWidth={2} />
              <span className="text-[11px] uppercase tracking-wide font-semibold text-amber-800">Based on your feedback</span>
            </div>
          )}

          {/* Body — collapsed shows hook preview only; expanded shows full prose. */}
          {expanded ? (
            <>
              {/* v5.1 P1.5 — "Still live from last cycle" italic line for
                  carry-forward actions. Subtle zinc-500 cue above body. */}
              {action.still_live_from_last_cycle === true && (
                <p
                  className="mt-3 italic text-zinc-500"
                  style={{ fontSize: "12px" }}
                  data-testid={`action-${index}-still-live`}
                >
                  Still live from last cycle
                </p>
              )}
              <div
                className="mt-3 paragraph-action-body text-zinc-900"
                style={{ fontSize: "15px", lineHeight: 1.65 }}
                data-testid={`action-body-${index}`}
              >
                <ReactMarkdown
                  components={{
                    p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-3 space-y-1" {...props} />,
                    ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-3 space-y-1" {...props} />,
                    li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
                    strong: ({ node, ...props }) => <strong className="font-semibold text-zinc-950" {...props} />,
                    h2: ({ node, ...props }) => <h2 className="mt-4 mb-1 text-[12px] font-semibold uppercase tracking-widest text-amber-800" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="mt-3 mb-1 text-[13px] font-semibold text-zinc-950" {...props} />,
                    // Disallow images, tables, raw HTML, hr inside body — strip
                    img: () => null,
                    table: () => null,
                    hr: () => null,
                    // Instruction 8 (2026-05-22) — inline citation-link
                    // injection is RETIRED. extractCitations() now always
                    // strips `[N]` markers from prose; the per-card source
                    // line under the title is the canonical citation, and
                    // the bottom ranked source list is the secondary view.
                    // Any remaining <a> elements come from the LLM-emitted
                    // markdown (rare for body prose) — render plain.
                    a: ({ node, href, children, ...props }) => {
                      const hrefStr = String(href || "");
                      if (hrefStr.startsWith("#cite-unavailable-")) {
                        // Legacy back-compat for older reveals stored with
                        // the sentinel href.
                        return (
                          <span
                            className="text-zinc-500"
                            title="Source unavailable — citation cannot be linked"
                          >
                            {children}
                          </span>
                        );
                      }
                      return (
                        <a
                          href={hrefStr}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-800 hover:text-amber-900 underline decoration-amber-300 hover:decoration-amber-700 underline-offset-2"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {cleaned}
                </ReactMarkdown>
              </div>

              {/* Citation chip row — REMOVED in Instruction 8 (2026-05-22).
                  Per-card citations now render via the anchor source line
                  under the title. The bottom ranked source list (with
                  inline "cited" pills) is the secondary view. */}
            </>
          ) : (
            <p
              className="mt-3 text-zinc-900 line-clamp-3"
              style={{ fontSize: "15px", lineHeight: 1.55 }}
              data-testid={`action-body-preview-${index}`}
            >
              {previewText}
            </p>
          )}

          {/* Toggle button */}
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={handleExpand}
              className="text-[13px] font-medium text-amber-700 hover:text-amber-800 transition-colors px-3 py-2 -mr-3 -my-2 min-h-[44px]"
              data-testid={`action-${index}-toggle`}
              aria-expanded={expanded}
            >
              {expanded ? "Show less" : "More..."}
            </button>
          </div>

          {/* Per-action feedback row (only on expanded state) */}
          {expanded && onActionFeedback && (
            <div className="mt-3 pt-3 border-t border-zinc-100">
              <OptionFeedbackRow
                currentState={actionFeedbackState}
                onSelect={(state) => onActionFeedback(action, state)}
                showHelper={index === 0}
                showLabels={index === 0}
                testIdBase={`action-${index}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function RAFActionCard({ action, index, mode, sources = [], selections = {}, onFeedback, isLite = false, actionAttribution = null, actionFeedbackState, onActionFeedback, expanded = false, onToggleExpand }) {
  // Wave 7 (Chunk 2.5) — branch on paragraph schema. If body_markdown is
  // present, render the new card. Otherwise fall back to the legacy RAF
  // options-based render below for back-compat with stored reveals.
  if (action.body_markdown) {
    return (
      <ParagraphActionCard
        action={action}
        index={index}
        sources={sources}
        actionAttribution={actionAttribution}
        actionFeedbackState={actionFeedbackState}
        onActionFeedback={onActionFeedback}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
      />
    );
  }

  const title = action.title || action.action_headline || action.headline || "";
  const thisWeek = action.this_week || action.action_this_week || "";
  const options = action.options || [];
  const whyBullets = action.why || [];
  // Instruction 9 (2026-05-22) — sequential render-time rank.
  const rank = index + 1;
  const hasNewFormat = whyBullets.length > 0;
  const hasOptions = options.length > 0;
  // Chunk 0 — `signal_anchor` hoists to action-level render. Forward-compat for the
  // Chunk 3 schema move: prefer `action.signal_anchor` when present, fall back to
  // the first option's anchor (today's live shape). Rendered ONCE under the title.
  const actionAnchor = action.signal_anchor || options?.[0]?.signal_anchor || null;
  const attachedActionSource = actionAttribution?.source || null;
  // Wave 13 — in lite paint, persona-calibrated surfaces haven't landed yet.
  const showOptionsShimmer = isLite && !hasOptions && !thisWeek;
  const showWhyShimmer = isLite && !hasNewFormat;

  // Old format fallback
  const oldContext = action.action_analysis_full || action.context || action.action_recommendation || "";
  const visibleBullets = mode === "quick" ? whyBullets.slice(0, 2) : mode === "coffee" ? whyBullets.slice(0, 3) : whyBullets;

  return (
    <div id={`action-${rank}`} className="border-l-4 border-l-amber-700 border border-zinc-200 bg-white p-5 animate-fade-in scroll-mt-24" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`action-card-${index}`}>
      <div className="flex items-start gap-4">
        {/* Wave 3h — prominent circular rank badge (was thin inline number) */}
        <span
          className="flex-shrink-0 inline-flex items-center justify-center rounded-full bg-zinc-950 text-white font-heading font-semibold mt-0.5 print:border print:border-zinc-950"
          style={{ width: "32px", height: "32px", fontSize: "15px", lineHeight: 1, letterSpacing: "-0.01em" }}
          aria-label={`Action ${rank}`}
          data-testid={`action-rank-badge-${index}`}
        >
          {rank}
        </span>
        <div className="flex-1 min-w-0">
          {/* Layer 1: Title */}
          <p style={{ fontSize: "17px", fontWeight: 600, color: "#111111", lineHeight: 1.4 }}>{title}</p>

          {/* Chunk 0 — action-level signal anchor + source (one per card, hoisted from options).
              Patch B — when the server flags action.anchor_suppressed=true (all
              cited sources were score=1), drop the whole anchor block: no
              orange anchor text, no outlet/date line. Citation chips at the
              bottom of the expanded body remain so rest-of-evidence integrity
              is preserved. */}
          {!action.anchor_suppressed && (actionAnchor || attachedActionSource) && (
            <div className="mt-1" data-testid={`action-anchor-${index}`}>
              {actionAnchor && (
                <p style={{ fontSize: "12px", fontWeight: 500, color: "#B45309" }} data-testid={`action-anchor-text-${index}`}>{actionAnchor}</p>
              )}
              {attachedActionSource && (
                <InlineSource
                  source={attachedActionSource}
                  shouldHyperlink={!!actionAttribution?.shouldHyperlink}
                  testId={`action-anchor-source-${index}`}
                />
              )}
            </div>
          )}

          {/* Wave 3f — admin-only debug chip showing which capability bets boosted this action. */}
          {Array.isArray(action.boosted_by_capability) && action.boosted_by_capability.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`action-boosted-chips-${index}`}>
              {action.boosted_by_capability.map((cap) => (
                <span
                  key={cap}
                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-medium bg-amber-50 text-amber-800 border border-amber-200"
                  title="Action ranked higher because this capability bet is selected"
                >
                  {cap}
                </span>
              ))}
            </div>
          )}

          {/* Layer 2: Options (new) or This Week (old) */}
          {hasOptions ? (
            <div className="mt-2.5" data-testid={`action-options-${index}`}>
              <p className="text-[12px] text-zinc-900 italic mb-2" data-testid={`action-options-helper-${index}`}>
                Not every option below will fit your Account context. Act on the ones that best match your account reality.
              </p>
              <div className="space-y-2">
                {options.map((opt, oi) => {
                  const rankVal = action.display_rank || action.action_rank || action.number || index + 1;
                  const fbKey = `${rankVal}:${oi}`;
                  const currentState = selections[fbKey];
                  // Wave 3h — a), b), c), d) option labels. Position-based.
                  const optionLabel = String.fromCharCode(97 + oi) + ")";
                  return (
                    <div key={oi} className={`bg-zinc-50 border px-3 py-2 ${opt.boosted_by_feedback ? "border-amber-300" : "border-zinc-100"}`} data-testid={`action-option-${index}-${oi}`}>
                      {opt.boosted_by_feedback && (
                        <div className="flex items-center gap-1 mb-1.5" data-testid={`action-option-${index}-${oi}-boosted-tag`}>
                          <Sparkles className="w-3 h-3 text-amber-700" strokeWidth={2} />
                          <span className="text-[11px] uppercase tracking-wide font-semibold text-amber-800">Based on your feedback</span>
                        </div>
                      )}
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p style={{ fontSize: "14px", fontWeight: 400, lineHeight: 1.5, color: "#1a1a1a" }}>
                            <span
                              className="font-semibold text-zinc-950 mr-1.5 select-none"
                              aria-hidden="true"
                              data-testid={`action-option-${index}-${oi}-label`}
                            >
                              {optionLabel}
                            </span>
                            {opt.option_text}
                          </p>
                          {/* Chunk 0 — per-option `signal_anchor` and InlineSource removed; both hoisted to action level. */}
                        </div>
                        {onFeedback && (
                          <div className="flex-shrink-0 mt-0.5">
                            <OptionFeedbackRow
                              currentState={currentState}
                              onSelect={(state) => onFeedback(action, oi, opt, state)}
                              showHelper={index === 0 && oi === 0}
                              showLabels={index === 0 && oi === 0}
                              testIdBase={`action-${index}-option-${oi}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : thisWeek ? (
            <div className="mt-2.5 bg-zinc-50 border border-zinc-100 px-3 py-2">
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#B45309" }}>This week: </span>
              <span style={{ fontSize: "14px", fontWeight: 400, color: "#1a1a1a" }}>{thisWeek}</span>
            </div>
          ) : showOptionsShimmer ? (
            <div className="mt-2.5 animate-pulse space-y-2" data-testid={`action-options-shimmer-${index}`}>
              <p className="text-[11px] text-zinc-500 italic">Calibrating for you…</p>
              <div className="bg-zinc-50 border border-zinc-100 px-3 py-3">
                <div className="h-3 bg-zinc-200/80 w-11/12 rounded-sm" />
                <div className="mt-1.5 h-3 bg-zinc-200/80 w-4/5 rounded-sm" />
              </div>
              <div className="bg-zinc-50 border border-zinc-100 px-3 py-3">
                <div className="h-3 bg-zinc-200/80 w-5/6 rounded-sm" />
                <div className="mt-1.5 h-3 bg-zinc-200/80 w-3/5 rounded-sm" />
              </div>
            </div>
          ) : null}

          {/* Layer 3: Why? */}
          {hasNewFormat ? (
            visibleBullets.length > 0 && (
              <div className="mt-2.5">
                <p style={{ fontSize: "13px", fontWeight: 600, color: "#111111" }}>Why?</p>
                <ul className="mt-1 space-y-1">
                  {visibleBullets.map((bullet, bi) => (
                    <li key={bi} className="flex items-start gap-2" style={{ fontSize: "14px", lineHeight: 1.5, color: "#111111" }}>
                      <span className="text-zinc-900 flex-shrink-0 mt-0.5">&#8226;</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : (
            oldContext && (
              <p className="mt-2" style={{ fontSize: "14px", fontWeight: 400, lineHeight: 1.6, color: "#111111" }}>{oldContext}</p>
            )
          )}
          {showWhyShimmer && (
            <div className="mt-2.5 animate-pulse" data-testid={`action-why-shimmer-${index}`}>
              <div className="h-3 bg-zinc-200/70 w-16 rounded-sm" />
              <div className="mt-2 h-3 bg-zinc-100 w-10/12 rounded-sm" />
              <div className="mt-1 h-3 bg-zinc-100 w-3/4 rounded-sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Wave 5: Merged "External context" section with multi-select category filter.
const CATEGORY_OPTIONS = [
  { key: "regulatory", label: "Regulatory" },
  { key: "market",     label: "Market" },
  { key: "competitor", label: "Competitor" },
  { key: "macro",      label: "Macro" },
];

function ExternalContextSection({ signals, accountName, weekLabel, mode, attributionMap = {} }) {
  const [active, setActive] = useState(new Set()); // empty set === "All"
  const allMode = active.size === 0;

  // Filter first, then (Wave 1) sort by severity+priority within filter.
  const filtered = allMode ? signals : signals.filter((s) => active.has(s.category));
  const sorted = sortBySeverity(filtered);
  const TOP_N = 3;
  const visible = sorted.slice(0, TOP_N);
  const hidden = sorted.slice(TOP_N);
  const drawerLabel = "More context";

  // Labels for empty state
  const emptyLabel = allMode
    ? "No signals this week for your profile."
    : (() => {
        const picked = CATEGORY_OPTIONS.filter((o) => active.has(o.key)).map((o) => o.label.toLowerCase());
        const cat = picked.length === 1 ? picked[0] : picked.join(" / ");
        return `No ${cat} signals this week for your profile.`;
      })();

  const togglePill = (key) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearAll = () => setActive(new Set());

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="section-external-context">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <p className="uppercase" style={SECTION_LABEL}>External context applied to this account</p>
      </div>
      {/* Sub-filter pills */}
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="external-filter">
        <button
          onClick={clearAll}
          className="px-3 py-1 transition-colors"
          style={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.02em",
            border: `1px solid ${allMode ? "#B45309" : "#E4E4E7"}`,
            backgroundColor: allMode ? "#FFFBEB" : "#FFFFFF",
            color: allMode ? "#92400E" : "#111111",
          }}
          data-testid="external-filter-all"
        >
          All
        </button>
        {CATEGORY_OPTIONS.map((opt) => {
          const isOn = active.has(opt.key);
          return (
            <button
              key={opt.key}
              onClick={() => togglePill(opt.key)}
              className="px-3 py-1 transition-colors"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                border: `1px solid ${isOn ? "#B45309" : "#E4E4E7"}`,
                backgroundColor: isOn ? "#FFFBEB" : "#FFFFFF",
                color: isOn ? "#92400E" : "#111111",
              }}
              data-testid={`external-filter-${opt.key}`}
              aria-pressed={isOn}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {sorted.length === 0 ? (
        <p className="text-zinc-900" style={{ fontSize: "14px" }} data-testid="external-context-empty">
          {emptyLabel}
        </p>
      ) : (
        <>
          <div className="space-y-3">
            {visible.map((s, i) => {
              const origIdx = signals.indexOf(s);
              return (
                <SignalCard key={`v-${s.headline}-${i}`} signal={s} index={i} accountName={accountName} weekLabel={weekLabel} mode={mode} isExternal attribution={attributionMap[origIdx]} />
              );
            })}
          </div>
          <MoreContextDrawer label={drawerLabel} hiddenCount={hidden.length} testId="external-context-drawer">
            {hidden.map((s, i) => {
              const origIdx = signals.indexOf(s);
              return (
                <SignalCard key={`d-${s.headline}-${i}`} signal={s} index={i + TOP_N} accountName={accountName} weekLabel={weekLabel} mode={mode} isExternal attribution={attributionMap[origIdx]} />
              );
            })}
          </MoreContextDrawer>
        </>
      )}
    </div>
  );
}

function CollapsibleSection({ label, defaultOpen, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-zinc-200" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-6 hover:bg-zinc-50 transition-colors" data-testid={`${testId}-toggle`}>
        <span className="uppercase" style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", color: "#111111" }}>{label}</span>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-900" /> : <ChevronDown className="w-4 h-4 text-zinc-900" />}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-6 pb-6 space-y-3">{children}</div>
      </div>
    </div>
  );
}

// Wave 2: inline drawer rendered beneath the top-N visible cards within a section.
// Visually secondary: thin hairline above the toggle, muted colour, no border around content.
function MoreContextDrawer({ label, hiddenCount, children, testId }) {
  const [open, setOpen] = useState(false);
  if (hiddenCount <= 0) return null;
  return (
    <div className="mt-4" data-testid={testId}>
      <div className="border-t border-zinc-100 pt-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between text-left hover:text-zinc-700 transition-colors"
          data-testid={`${testId}-toggle`}
          aria-expanded={open}
        >
          <span style={{ fontSize: "12px", fontWeight: 600, color: "#111111", letterSpacing: "0.02em" }}>
            {label} ({hiddenCount} more)
          </span>
          {open ? <ChevronUp className="w-3.5 h-3.5 text-zinc-900" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-900" />}
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[5000px] opacity-100 mt-3" : "max-h-0 opacity-0 mt-0"}`}
          data-testid={`${testId}-content`}
        >
          <div className="space-y-3">{children}</div>
        </div>
      </div>
    </div>
  );
}

const SECTION_LABEL = { fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", color: "#111111" };

// =============================================================================
// Chunk 3 — SourcesList
// Folded by default. Two groups: "Used in cross-connection" (sources cited in
// any cross_connected action's body or referenced as a cross_connected action's
// Chunk 1 / Task 2 — classify sources by whether they appear in any action's
// cited_sources (post-remap) rather than the cross_connected flag.
function classifySourcesByCitedSources(actions, sources) {
  const usedNs = new Set();
  if (!Array.isArray(sources) || sources.length === 0) return { usedNs };
  (actions || []).forEach((a) => {
    (a?.cited_sources || []).forEach((idx) => {
      const n = parseInt(idx, 10);
      if (n >= 1 && n <= sources.length) usedNs.add(n);
    });
  });
  return { usedNs };
}

// Chunk 1 / Task 2 — helper: returns true if a URL is a Google-internal redirect
// that should never be shown to users.
function isGoogleRedirectUrl(url) {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return host.includes("vertexaisearch.cloud.google.com") || host.includes("googleusercontent.com");
  } catch { return false; }
}

function SourceRow({ n, source, isCited = false }) {
  if (!source) return null;
  const verified = source.url_verified !== false;
  // Chunk 1 / Task 2 — never show vertexaisearch / googleusercontent as a link
  const isGoogleRedirect = isGoogleRedirectUrl(source.url);
  // Chunk 3.6 — Wayback fallback: when the original URL is not verified
  // but a wayback snapshot was stamped, use the archive URL for the link
  // and surface a subtle "(archived)" suffix.
  const hasArchive = !verified && !!source.wayback_url;
  const linkUrl = verified ? source.url : (hasArchive ? source.wayback_url : null);
  const isClickable = !!linkUrl && !isGoogleRedirect;

  let domain = "";
  try { if (source.url) domain = new URL(source.url).hostname.replace(/^www\./, ""); } catch { /* noop */ }
  const outlet = source.source_name || domain;
  const displayTitle = source.title || outlet || "Untitled";

  return (
    <li id={`source-${n}`} className="flex gap-3 py-2 scroll-mt-24" data-testid={`source-row-${n}`}>
      <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 text-[11px] font-semibold border border-amber-200 bg-amber-50 text-amber-800 self-start mt-0.5">
        [{n}]
      </span>
      <div className="flex-1 min-w-0">
        {isClickable ? (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-[14px] font-semibold text-zinc-950 hover:text-amber-800 transition-colors leading-snug"
            data-testid={`source-row-${n}-link`}
          >
            {displayTitle}
          </a>
        ) : (
          <p className="text-[14px] font-semibold text-zinc-950 leading-snug" data-testid={`source-row-${n}-text`}>
            {displayTitle}
          </p>
        )}
        <p className="mt-0.5 text-[12px] text-zinc-700">
          {(outlet || source.date) ? (
            [outlet, source.date].filter(Boolean).join(" · ")
          ) : (
            <span className="italic text-zinc-500">source name not captured</span>
          )}
          {hasArchive && (
            <span
              className="ml-1.5 text-zinc-500 italic"
              title="Original URL returned 404/403; linked via archive.org Wayback Machine snapshot."
              data-testid={`source-row-${n}-archive-tag`}
            >
              (archived)
            </span>
          )}
          {isCited && (
            <span
              className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200"
              data-testid={`source-row-${n}-cited-pill`}
              title="An action card in this Reveal is anchored to this source."
            >
              cited
            </span>
          )}
        </p>
      </div>
    </li>
  );
}

// Instruction 8 (2026-05-22), Change B1+B3+B4.
// Single ranked list, no USED/OTHER split. Backend has already ordered
// `sources[]` by `source_tier_bucket` (account_origin > account_named >
// peer > regulatory_other) and stamped each source with its bucket. We
// render bucket dividers; cited entries get an inline "cited" pill.
const BUCKET_LABELS = {
  account_origin: "From the account directly",
  account_named: "Where the account is named",
  peer: "Peer & vendor signals",
  regulatory_other: "Regulatory & wider context",
};
const BUCKET_ORDER = ["account_origin", "account_named", "peer", "regulatory_other"];

function _citedAnchorHeadlines(actions) {
  const ids = new Set();
  (actions || []).forEach((a) => {
    if (a && a.anchor_signal && a.anchor_signal.headline) {
      ids.add(String(a.anchor_signal.headline).toLowerCase().trim());
    }
  });
  return ids;
}

function SourcesList({ actions, sources }) {
  const [open, setOpen] = useState(false);

  // Chunk 1 / Task 2 — deduplicate by outlet+title before grouping
  const deduped = useMemo(() => {
    const seen = new Set();
    return (sources || []).filter((s) => {
      const key = `${s.source_name || ""}||${s.title || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [sources]);

  const total = deduped.length;
  const citedHeadlines = useMemo(() => _citedAnchorHeadlines(actions), [actions]);

  // Group by source_tier_bucket. Falls back to regulatory_other for
  // entries lacking the stamp (legacy reveals pre-Instruction-8).
  const grouped = useMemo(() => {
    const out = { account_origin: [], account_named: [], peer: [], regulatory_other: [] };
    deduped.forEach((s, i) => {
      const bucket = (s && s.source_tier_bucket && BUCKET_LABELS[s.source_tier_bucket])
        ? s.source_tier_bucket
        : "regulatory_other";
      out[bucket].push({ n: i + 1, source: s });
    });
    return out;
  }, [deduped]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("reveal:open-sources", handler);
    return () => window.removeEventListener("reveal:open-sources", handler);
  }, []);

  if (total === 0) return null;

  return (
    <div className="bg-white border border-zinc-200" data-testid="sources-list">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-6 hover:bg-zinc-50 transition-colors"
        aria-expanded={open}
        data-testid="sources-list-toggle"
      >
        <span className="uppercase" style={SECTION_LABEL}>Sources used ({total})</span>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-900" /> : <ChevronDown className="w-4 h-4 text-zinc-900" />}
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[6000px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-6 pb-6">
          <p className="italic text-[12px] text-zinc-700 mb-4 leading-relaxed" data-testid="sources-list-humility-line">
            These are the public signals we read this week to shape your Reveal. The half you bring is the private context inside the account.
          </p>
          {BUCKET_ORDER.map((bucket) => {
            const entries = grouped[bucket];
            if (!entries || entries.length === 0) return null;
            return (
              <div className="mb-5 last:mb-0" key={bucket} data-testid={`sources-bucket-${bucket}`}>
                <p className="uppercase mb-2 text-[11px] font-semibold tracking-widest text-amber-800">
                  {BUCKET_LABELS[bucket]} ({entries.length})
                </p>
                <ul className="divide-y divide-zinc-100">
                  {entries.map(({ n, source }) => {
                    const isCited = source && source.title
                      ? citedHeadlines.has(String(source.title).toLowerCase().trim())
                      : false;
                    return (
                      <SourceRow key={`${bucket}-${n}`} n={n} source={source} isCited={isCited} />
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { TimeModeToggle };

export default function RevealRenderer({ reveal, accountName = "Deutsche Bank", weekLabel = "", mode = "coffee", accountId = "", revealCycle = "", isLite = false }) {
  // Wave 12 — feedback selections (keyed "actionRank:optionIndex" → state)
  const [feedbackSelections, setFeedbackSelections] = useState({});
  // Wave 7 (Chunk 2.5) — per-action feedback for paragraph schema (keyed "actionRank" → state)
  const [actionFeedbackSelections, setActionFeedbackSelections] = useState({});
  const [adjustmentCounts, setAdjustmentCounts] = useState(null);

  // v5.1 (post-Chunk-4) — hoisted expansion state, keyed by card index.
  // - On mode change, cards the user has NOT manually toggled are reset
  //   to the new mode's default (Quick → all collapsed; Coffee → #1 only;
  //   Deep → #1 and #2). Cards the user HAS manually toggled keep their
  //   user-set state across mode switches.
  // - manuallyToggled[i]=true means "respect user choice; don't reset on mode change".
  const [expandedByIndex, setExpandedByIndex] = useState({});
  const manuallyToggledRef = useRef({});

  const getModeDefault = (i, m) => {
    if (m === "quick") return false;
    if (m === "coffee") return i === 0;
    return i < 2;   // deep
  };

  // Reset (preserving manual toggles) whenever the mode changes.
  useEffect(() => {
    setExpandedByIndex((prev) => {
      const next = {};
      // Walk a generous range — totalActions can be up to 8 from the calibrator.
      for (let i = 0; i < 16; i += 1) {
        if (manuallyToggledRef.current[i]) {
          next[i] = prev[i] ?? getModeDefault(i, mode);
        } else {
          next[i] = getModeDefault(i, mode);
        }
      }
      return next;
    });
  }, [mode]);

  const toggleExpanded = (i) => {
    manuallyToggledRef.current[i] = true;
    setExpandedByIndex((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  // Wave 15 — resolve source attribution + dedup in one pass, BEFORE React renders children.
  // For every signal/option, compute the matched source index and a `shouldHyperlink: bool`
  // flag. Priority walk: account_signals → external_signals → actions.
  // Only the FIRST card encountered per-URL gets the hyperlink. This object is then
  // read by child components (no mutable state-passing during render).
  //
  // Chunk 0 (2026-04-23) — actions now attribute ONCE per action (not per option).
  // `signal_anchor` hoists to action-level render. `actionAttr[actionIdx]` is a single
  // `{source, shouldHyperlink}` (or null), not a per-option array.
  const sourceAttribution = (() => {
    const sources = reveal?.sources || [];
    const used = new Set();
    const accountAttr = [];     // [{ source, shouldHyperlink }] by signal idx
    const externalAttr = {};    // { idx: {source, shouldHyperlink} } keyed by external_signals index
    const actionAttr = [];      // [{source, shouldHyperlink}, ...] one entry per action

    const resolve = (queryText) => {
      const idx = matchSourcesForText(queryText, sources)[0];
      if (typeof idx !== "number") return null;
      const s = sources[idx];
      if (!s) return null;
      let shouldHyperlink = false;
      // Chunk 3.6 — an archived source (wayback_url present) is also
      // hyperlinkable; dedup on the ORIGINAL url so an archive and a
      // verified source for the same article share one hyperlink slot.
      const canLink = s.url && (s.url_verified !== false || !!s.wayback_url);
      if (canLink && !used.has(s.url)) {
        shouldHyperlink = true;
        used.add(s.url);
      }
      return { source: s, shouldHyperlink };
    };

    (reveal?.account_signals || []).forEach((sig) => {
      const impl = parseImplication(sig.implication).text || "";
      accountAttr.push(resolve([sig.headline, sig.context, impl].filter(Boolean).join(" ")));
    });
    (reveal?.external_signals || []).forEach((sig, i) => {
      externalAttr[i] = resolve([sig.headline, sig.context].filter(Boolean).join(" "));
    });
    (reveal?.actions || []).forEach((a) => {
      // Patch B — if the server computed an explicit anchor_source_index
      // (picking the highest-scoring non-content-farm cited source), use
      // that directly. Otherwise fall back to text matching.
      if (a.anchor_suppressed) {
        actionAttr.push(null);
        return;
      }
      const idx = (typeof a.anchor_source_index === "number" && a.anchor_source_index >= 1)
        ? a.anchor_source_index - 1
        : null;
      if (idx !== null && sources[idx]) {
        const s = sources[idx];
        let shouldHyperlink = false;
        if (s.url && s.url_verified !== false && !used.has(s.url)) {
          shouldHyperlink = true;
          used.add(s.url);
        }
        actionAttr.push({ source: s, shouldHyperlink });
        return;
      }
      // Wave 7 (Chunk 2.5) — paragraph-schema actions carry primary_signal_anchor.
      // Forward-compat: action-level signal_anchor (Chunk 3 schema move).
      // Fall back to options[0].signal_anchor (legacy RAF) — and to body_markdown
      // first-line for paragraph actions whose anchor field is empty.
      const anchor = a.primary_signal_anchor || a.signal_anchor || a.options?.[0]?.signal_anchor || "";
      const firstOptText = a.options?.[0]?.option_text || "";
      const bodyHead = (a.body_markdown || "").split("\n\n")[0] || "";
      actionAttr.push(resolve(`${firstOptText} ${bodyHead} ${anchor}`));
    });
    return { accountAttr, externalAttr, actionAttr };
  })();

  // Wave 12 — load existing selections + adjustment summary for this reveal
  useEffect(() => {
    if (!accountId || !revealCycle) return;
    API.get(`/feedback/option?account_id=${accountId}&reveal_cycle=${revealCycle}`)
      .then(({ data }) => setFeedbackSelections(data?.selections || {}))
      .catch(() => {});
    // Wave 7 — also load per-action feedback for paragraph-schema reveals.
    API.get(`/feedback/action?account_id=${accountId}&reveal_cycle=${revealCycle}`)
      .then(({ data }) => setActionFeedbackSelections(data?.selections || {}))
      .catch(() => {});
    API.get("/feedback/adjustments")
      .then(({ data }) => setAdjustmentCounts(data))
      .catch(() => {});
  }, [accountId, revealCycle]);

  // Wave 7 (Chunk 2.5) — per-action feedback handler for paragraph schema.
  const handleActionFeedback = async (action, newState) => {
    const rank = String(action.display_rank || action.action_rank || action.rank || 0);
    const currentState = actionFeedbackSelections[rank];
    const willToggleOff = currentState === newState;

    setActionFeedbackSelections((prev) => {
      const next = { ...prev };
      if (willToggleOff) delete next[rank];
      else next[rank] = newState;
      return next;
    });

    try {
      await API.post("/feedback/action", {
        account_id: accountId,
        reveal_cycle: revealCycle,
        action_rank: action.display_rank || action.action_rank || action.rank || 0,
        action_title: action.title || "",
        primary_signal_anchor: action.primary_signal_anchor || action.signal_anchor || "",
        company_types: action.company_types || [],
        motions: action.motions || [],
        capability_tags: action.capability_tags || [],
        state: newState,
      });
    } catch (err) {
      // Roll back on failure
      setActionFeedbackSelections((prev) => {
        const next = { ...prev };
        if (currentState) next[rank] = currentState;
        else delete next[rank];
        return next;
      });
    }
  };

  const handleFeedback = async (action, optionIndex, option, newState) => {
    const key = `${action.display_rank || action.action_rank || action.rank}:${optionIndex}`;
    const currentState = feedbackSelections[key];
    const willToggleOff = currentState === newState;

    // Optimistic update
    setFeedbackSelections((prev) => {
      const next = { ...prev };
      if (willToggleOff) delete next[key];
      else next[key] = newState;
      return next;
    });

    try {
      const { data } = await API.post("/feedback/option", {
        account_id: accountId,
        reveal_cycle: revealCycle,
        action_rank: action.display_rank || action.action_rank || action.rank || 0,
        action_title: action.title || action.action_headline || "",
        option_index: optionIndex,
        option_text: option.option_text || "",
        signal_anchor: option.signal_anchor || "",
        company_types: action.company_types || [],
        motions: action.motions || [],
        state: newState,
      });
      // If a shape just flipped to downgraded/restored, refresh the banner
      if (data.shape_status_changed) {
        API.get("/feedback/adjustments")
          .then(({ data: adj }) => setAdjustmentCounts(adj))
          .catch(() => {});
      }
    } catch {
      // revert on failure
      setFeedbackSelections((prev) => {
        const next = { ...prev };
        if (willToggleOff) next[key] = currentState;
        else if (currentState) next[key] = currentState;
        else delete next[key];
        return next;
      });
    }
  };

  if (!reveal) return null;

  const allActions = reveal.actions || [];
  // Chunk 3 — time-mode filters card visibility (3 / 5 / all). Score order
  // preserved (cards arrive pre-sorted from calibrate_reveal); never reordered.
  const cardLimit = mode === "quick" ? 3 : mode === "coffee" ? 5 : 999;
  const visibleActions = allActions.slice(0, cardLimit);
  const totalActions = allActions.length;
  const visibleCount = visibleActions.length;

  return (
    <div className="space-y-6" data-testid="reveal-renderer">
      {/* Actions — primary surface */}
      {visibleActions.length > 0 && (
        <div className="bg-white border border-zinc-200 p-6" data-testid="section-actions">
          <p className="uppercase mb-4" style={SECTION_LABEL} data-testid="actions-counter">
            {/* Instruction 10 (2026-05-22) — vanity count "N of N" removed.
                Card count is implied by the visible card list. The
                thin-cycle note remains when relevant. */}
            Actions this week
            {reveal?.thin_evidence_week === true && totalActions > 0 && (
              <span className="ml-2 normal-case tracking-normal text-zinc-500" style={{ letterSpacing: 0, fontWeight: 400 }} data-testid="thin-week-header-disclosure">
                — lighter set than usual this cycle — public signal week was thin
              </span>
            )}
            {/* v5.1 (post-Chunk-4) — Quick mode hint when more cards exist
                in deeper modes. Subtle, only renders when toggling to a
                deeper mode would actually surface more. */}
            {totalActions >= 3 && mode === "quick" && totalActions > visibleCount && (
              <span className="ml-2 normal-case tracking-normal text-zinc-500" style={{ letterSpacing: 0, fontWeight: 400 }} data-testid="actions-counter-quick-hint">
                · {totalActions - visibleCount} more in Coffee / Deep
              </span>
            )}
          </p>

          {/* Wave 12 — "Kalib AI adjusted" banner. Hidden in v5.1 (P0.6) since
              feedback is recording-only until FEEDBACK_CALIBRATION_ENABLED
              flips true on the backend. */}
          {false && ((reveal?.feedback_applied_on_this_reveal === true) || (adjustmentCounts && adjustmentCounts.active_count > 0)) && (
            <div
              className="mb-4 flex items-start gap-2 px-3 py-2 bg-amber-50/60 border-l-2 border-l-amber-700/60"
              data-testid="kalib-adjusted-note"
              role="status"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-700 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <p className="text-[12px] text-zinc-700 leading-relaxed">
                Kalib AI adjusted based on your feedback.
                {adjustmentCounts && (adjustmentCounts.downgraded > 0 || adjustmentCounts.restored > 0) && (
                  <span className="text-zinc-900">
                    {" "}
                    {adjustmentCounts.downgraded > 0 && `${adjustmentCounts.downgraded} option shape${adjustmentCounts.downgraded > 1 ? "s" : ""} suppressed`}
                    {adjustmentCounts.downgraded > 0 && adjustmentCounts.restored > 0 && " · "}
                    {adjustmentCounts.restored > 0 && `${adjustmentCounts.restored} restored`}.
                  </span>
                )}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {visibleActions.map((a, i) => (
              <RAFActionCard
                key={i}
                action={a}
                index={i}
                mode={mode}
                sources={reveal.sources || []}
                selections={feedbackSelections}
                onFeedback={handleFeedback}
                isLite={isLite}
                actionAttribution={sourceAttribution.actionAttr[i] || null}
                actionFeedbackState={actionFeedbackSelections[String(a.display_rank || a.action_rank || (i + 1))]}
                onActionFeedback={handleActionFeedback}
                expanded={expandedByIndex[i] ?? (mode === "coffee" ? i === 0 : mode === "deep" ? i < 2 : false)}
                onToggleExpand={() => toggleExpanded(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Chunk 3 — Sources list at bottom (folded by default) */}
      <SourcesList actions={allActions} sources={reveal.sources || []} />

      {/* Chunk 3 — "What is moving at this account" U-signals section + External
          context section removed. Their content is now folded into the action
          cards (via body_markdown) and the sources list (via citation chips). */}
    </div>
  );
}
